import React, { useEffect, useRef } from "react";
import { CheckCircle, AlertCircle, XCircle, Loader2, Info } from "lucide-react";
import { useStatusBarStore, getColorForType } from "../stores/statusBarStore";

const StatusBar: React.FC = () => {
  const { message, type, isVisible, icon, isConnected, lastChecked, actions } = useStatusBarStore();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Initial check
    actions.checkBackendConnection();

    // Set up periodic checking every 30 seconds
    intervalRef.current = setInterval(() => {
      actions.checkBackendConnection();
    }, 30000);

    // Cleanup on unmount
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [actions]);

  if (!isVisible) return null;

  const bgColor = getColorForType(type);

  // Get appropriate icon component
  const getIconComponent = () => {
    switch (type) {
      case "success":
        return <CheckCircle className="h-5 w-5 mr-3 text-green-100" />;
      case "error":
        return <XCircle className="h-5 w-5 mr-3 text-red-100" />;
      case "warning":
        return <AlertCircle className="h-5 w-5 mr-3 text-yellow-100" />;
      case "info":
        return <Info className="h-5 w-5 mr-3 text-blue-100" />;
      case "loading":
        return <Loader2 className="h-5 w-5 mr-3 text-blue-100 animate-spin" />;
      default:
        return <CheckCircle className="h-5 w-5 mr-3 text-green-100" />;
    }
  };

  return (
    <div
      className={`${bgColor} px-6 py-3 flex items-center justify-center transition-all duration-300 border-b border-slate-700`}
    >
      {getIconComponent()}
      <span className="text-sm font-medium text-center text-green-100">
        {message}
      </span>
      {lastChecked && (
        <span className="text-xs text-green-200 ml-3 opacity-75">
          Last checked: {lastChecked.toLocaleTimeString()}
        </span>
      )}
    </div>
  );
};

export default StatusBar;
